/**
 * Private Parties blog – vanilla JS: post loading, filters, selected locations
 * Loaded by sections/private-parties.liquid
 */
(function () {
  'use strict';

  const win = /** @type {Window & { shopify?: { blog?: { handle?: string } } }} */ (typeof window !== 'undefined' ? window : {});
  /** @type {Window & { filter_restaurant?: function(): void, filter_restaurant_cuisine?: function(): void, filter_restaurant_space?: function(): void, showResults?: function(): void, showResultsAll?: function(): void }} */
  const globalWindow = typeof window !== 'undefined' ? window : /** @type {any} */ ({});

  const shopify = win.shopify;
  const BLOG_HANDLE = (shopify && shopify.blog && shopify.blog.handle)
    ? shopify.blog.handle
    : 'private-events';

  const INITIAL_VISIBLE = 6;
  const LOAD_MORE_COUNT = 3;

  // ----- Persistent state (sessionStorage) -----
  const STORAGE_AVAILABLE = typeof window !== 'undefined' && !!window.sessionStorage;
  const STATE_KEY = STORAGE_AVAILABLE ? 'pp_blog_state_' + BLOG_HANDLE : null;
  /** @type {{ visibleCount?: number, scrollY?: number } | null} */
  let initialState = null;

  if (STATE_KEY) {
    try {
      const raw = window.sessionStorage.getItem(STATE_KEY);
      if (raw) {
        initialState = JSON.parse(raw);
      }
    } catch (_) {
      initialState = null;
    }
  }

  let visibleCount = INITIAL_VISIBLE;
  if (initialState && typeof initialState.visibleCount === 'number' && initialState.visibleCount > 0) {
    visibleCount = initialState.visibleCount;
  }

  // ----- Helpers -----
  function qs(sel, root = document) {
    return root.querySelector(sel);
  }
  function qsAll(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  }
  function on(target, event, selector, handler) {
    target.addEventListener(event, (e) => {
      const el = e.target.closest(selector);
      if (el) handler.call(el, e);
    });
  }

  function getArticleUrl(handle) {
    if (!handle || typeof handle !== 'string') return '#';
    // Handle may already include blog path (e.g. "private-events/davanti-enoteca") – strip it to avoid duplicate
    const articleHandle = handle.replace(new RegExp('^' + BLOG_HANDLE + '/?'), '').trim() || handle;
    return `/blogs/${BLOG_HANDLE}/${articleHandle}`;
  }

  function escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Check whether a guest count falls within a "min-max" range string (e.g. "10-150").
   * Falls back to substring match for non-range formats.
   */
  function guestInRange(rangeStr, searchValue) {
    const n = parseInt(searchValue, 10);
    if (isNaN(n)) return false;
    const match = String(rangeStr).match(/(\d+)\s*[-\u2013]\s*(\d+)/);
    if (match) {
      const min = parseInt(match[1], 10);
      const max = parseInt(match[2], 10);
      return n >= min && n <= max;
    }
    return String(rangeStr).toLowerCase().indexOf(String(n)) !== -1;
  }

  /** Return a low-res version of a Shopify CDN image URL for blur-up placeholder */
  function getLowResImageUrl(fullUrl, width) {
    if (!fullUrl || typeof fullUrl !== 'string') return '';
    const w = width || 80;
    return fullUrl.indexOf('?') !== -1
      ? fullUrl + '&width=' + w
      : fullUrl + '?width=' + w;
  }

  /**
   * Build a single article card HTML from data (one source of truth for card markup).
   */
  function buildArticleCard(article) {
    const url = getArticleUrl(article.handle);
    const title = escapeHtml(article.restaurant_title || article.title || '');
    /** Location may include HTML (e.g. <br>) – raw for display */
    const locationHtml = article.restaurant_location || '-';
    const cuisine = escapeHtml(article.restaurant_cuisine || '-');
    const guests = escapeHtml(article.restaurant_guests || '');
    const space = escapeHtml(article.restaurant_space || '');
    /** Price is trusted content and may include HTML (e.g. <span>$$</span>) */
    const priceHtml = article.restaurant_price || '';
    const imgSrc = article.image && article.image.src ? escapeHtml(article.image.src) : '';
    const imgSrcLow = getLowResImageUrl(article.image && article.image.src ? article.image.src : '', 80);
    /** For CSS url() in inline style: escape backslash and single quote only (do not HTML-escape or & breaks) */
    const imgSrcLowForStyle = imgSrcLow ? String(imgSrcLow).replace(/\\/g, '\\\\').replace(/'/g, "\\'") : '';
    const imgStatus = (article.image && article.image.src_status) ? escapeHtml(article.image.src_status) : '';
    const logoStatus = article.restaurant_logo_status ? escapeHtml(article.restaurant_logo_status) : '';
    const logoUrl = typeof article.restaurant_logo === 'string' ? article.restaurant_logo : (article.restaurant_logo && (article.restaurant_logo.src || article.restaurant_logo.url)) || '';
    const logo = logoUrl.length ? escapeHtml(logoUrl) : '';
    const logoObj = article.restaurant_logo && typeof article.restaurant_logo === 'object' ? article.restaurant_logo : null;
    const logoAlt = (logoObj && logoObj.alt) ? escapeHtml(logoObj.alt) : title;

    return `
      <article 
        class="blog-article-card" 
        data-article-id="${escapeHtml(String(article.id))}"
      >
        <div 
          class="blog-article-card-wrapper" 
          data-restaurant-title="${title}" 
          data-restaurant_location="${escapeHtml(article.restaurant_location || '')}" 
          data-restaurant_guests="${escapeHtml(article.restaurant_guests || '')}" 
          data-restaurant_space="${escapeHtml(article.restaurant_space || '')}"
        >
          <div class="blog-article-card-featured-image ${imgStatus}">
            <a 
              class="blog-article-card-image-link" 
              href="${url}"
              rel="nofollow"
              title="View details"
              aria-label="View details for ${escapeHtml(title)}"
            >
              <div class="blog-article-card-image-wrapper blog-article-card-image-wrapper--blur-up">
                <div class="blog-article-card-image-placeholder" style="${imgSrcLowForStyle ? 'background-image:url(\'' + imgSrcLowForStyle + '\')' : ''};" aria-hidden="true"></div>
                <img 
                  class="blog-card-img" 
                  src="${imgSrc}" 
                  alt="${article.image && article.image.alt ? escapeHtml(article.image.alt) : title}"
                  loading="lazy"
                  fetchpriority="low"
                  width="${article.image && article.image.width ? article.image.width : 600}"
                  height="${article.image && article.image.height ? article.image.height : 400}"
                >
              </div>
            </a>
            <div class="blog-article-card-logo ${logoStatus}">
              <img 
                src="${logo}" 
                loading="lazy"
                width="250"
                alt="${logoAlt} - Restaurant Logo"
              />
            </div>
          </div>
          <div class="blog-article-card-content">
            <div class="blog-article-card-content-wrapper">
              <div class="blog-article-card-content-item">
                <div class="blog-card-location"><span class="blog-card-location-label">location</span><span class="blog-card-location-content">${locationHtml}</span></div>
              </div>
              <div class="blog-article-card-content-item">
                <div class="blog-card-cuisine"><span class="blog-card-cuisine-label">cuisine</span><span class="blog-card-cuisine-content">${cuisine}</span></div>
              </div>
              <div class="blog-article-card-content-item">
                <div class="blog-card-bottom">
                  <div class="blog-card-bottom-item blog-card-bottom-item-guests">
                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="https://www.w3.org/2000/svg"><path d="M15.7035 3.5625C15.5891 5.15117 14.4106 6.375 13.1254 6.375C11.8403 6.375 10.6598 5.15156 10.5473 3.5625C10.4301 1.90977 11.5774 0.75 13.1254 0.75C14.6735 0.75 15.8207 1.93984 15.7035 3.5625Z" stroke="#CFC8BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M13.1248 8.875C10.5791 8.875 8.1311 10.1395 7.51781 12.602C7.43656 12.9277 7.64086 13.25 7.97563 13.25H18.2745C18.6092 13.25 18.8123 12.9277 18.7323 12.602C18.119 10.1 15.6709 8.875 13.1248 8.875Z" stroke="#CFC8BD" stroke-width="1.5" stroke-miterlimit="10"></path><path d="M7.81214 4.26328C7.72073 5.53203 6.76839 6.53125 5.74183 6.53125C4.71526 6.53125 3.76136 5.53242 3.67152 4.26328C3.57816 2.94336 4.50511 2 5.74183 2C6.97854 2 7.9055 2.96758 7.81214 4.26328Z" stroke="#CFC8BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.04725 8.95313C7.34218 8.63008 6.56561 8.50586 5.74257 8.50586C3.71132 8.50586 1.75429 9.51562 1.26405 11.4824C1.1996 11.7426 1.36288 12 1.63007 12H6.016" stroke="#CFC8BD" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"></path></svg>
                    <h7 class="blog-card-bottom-item-guests-value">${guests}</h7>
                  </div>
                  <div class="blog-card-bottom-item blog-card-bottom-item-price">
                    <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="https://www.w3.org/2000/svg"><path d="M1.87501 11.125L18.125 11.125C18.4702 11.125 18.75 10.8452 18.75 10.5V1.75C18.75 1.40482 18.4702 1.125 18.125 1.125L1.87501 1.125C1.52983 1.125 1.25001 1.40482 1.25001 1.75V10.5C1.25001 10.8452 1.52983 11.125 1.87501 11.125Z" stroke="#CFC8BD" stroke-width="1.5" stroke-linejoin="round"></path><path d="M2.5 13H17.5" stroke="#CFC8BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M3.75 14.875H16.25" stroke="#CFC8BD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    <h7 class="blog-card-bottom-item-price-value">${priceHtml}</h7>
                  </div>
                </div>
              </div>
              <div class="blog-article-card-content-item">
                <div 
                  class="blog-card-see-details"
                >
                  <a 
                    class="btn" 
                    href="${url}"
                    rel="nofollow"
                    title="See details - ${title}"
                    aria-label="See details - ${title}"
                  >
                    See details
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>`
    ;
  }

  function markBlurUpLoaded(container) {
    if (!container) return;
    container.querySelectorAll('.blog-article-card-image-wrapper--blur-up .blog-card-img').forEach(function (img) {
      if (img.complete) {
        const wrapper = img.closest('.blog-article-card-image-wrapper--blur-up');
        if (wrapper) wrapper.classList.add('is-loaded');
      }
    });
  }

  function renderArticles(container, articles, visibleCount) {
    if (!container) return;
    const count = Number.isFinite(visibleCount) ? visibleCount : articles.length;
    const html = articles.slice(0, count).map(buildArticleCard).join('');
    container.innerHTML = html || '<div class="empty_box_msg">No results found</div>';
    setLoadMoreVisibility(container, articles.length, count);
    markBlurUpLoaded(container);
  }

  /** Find load-more button: next sibling of #AjaxinateLoop or fallback to .load-more */
  function getLoadMoreBtn() {
    const loop = document.getElementById('AjaxinateLoop');
    if (loop && loop.nextElementSibling) {
      const btn = qs('.load-more', loop.nextElementSibling);
      if (btn) return btn;
    }
    return qs('.load-more');
  }

  function setLoadMoreVisibility(container, total, visible) {
    const loadMore = getLoadMoreBtn();
    if (!loadMore) return;
    if (total > visible) {
      loadMore.style.visibility = '';
      loadMore.removeAttribute('style');
    } else {
      loadMore.style.visibility = 'hidden';
    }
  }

  function showFirstN(container, n) {
    if (!container) return;
    const articles = qsAll('article.blog-article-card', container);
    const count = Number.isFinite(n) ? n : INITIAL_VISIBLE;
    visibleCount = count;
    articles.forEach((el, i) => {
      el.style.display = i < count ? '' : 'none';
    });
    setLoadMoreVisibility(container, articles.length, count);
  }

  function saveState(extra) {
    if (!STATE_KEY || !STORAGE_AVAILABLE) return;
    try {
      const state = Object.assign(
        {
          visibleCount,
          scrollY: window.scrollY || 0
        },
        extra || {}
      );
      window.sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (_) {
      // ignore storage errors
    }
  }

  // ----- Data & state -----
  let datum = [];
  let loopEl = null;

  function getData() {
    const dataEl = document.getElementById('blog_article_data');
    if (!dataEl || !dataEl.textContent.trim()) return [];
    try {
      return JSON.parse(dataEl.textContent);
    } catch (_) {
      return [];
    }
  }

  /**
   * Modernized initial load: parse JSON once, render with a single innerHTML pass, then show first N.
   */
  function loadPosts() {
    loopEl = document.getElementById('AjaxinateLoop');
    if (!loopEl) return;
    datum = getData();
    if (!Array.isArray(datum) || datum.length === 0) {
      loopEl.innerHTML = '<div class="empty_box_msg">No results found</div>';
      return;
    }
    const html = datum.map(buildArticleCard).join('');
    loopEl.innerHTML = html;
    const articles = qsAll('article.blog-article-card', loopEl);
    const initialVisible = (initialState && typeof initialState.visibleCount === 'number' && initialState.visibleCount > 0)
      ? initialState.visibleCount
      : INITIAL_VISIBLE;
    showFirstN(loopEl, initialVisible);
    saveState();

    if (initialState && typeof initialState.scrollY === 'number' && initialState.scrollY > 0) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, initialState.scrollY || 0);
      });
    }
  }

  function counterResults(showresults) {
    if (!loopEl) return;
    if (showresults.count > 0) {
      loopEl.innerHTML = showresults.results;
      showFirstN(loopEl, INITIAL_VISIBLE);
      saveState();
    } else {
      loopEl.innerHTML = '<div class="empty_box_msg">No results found</div>';
      const loadMore = getLoadMoreBtn();
      if (loadMore) loadMore.style.visibility = 'hidden';
    }
  }

  function prepareRestaurantFilterData(data, selectedItems) {
    let results = '';
    let count = 0;
    data.forEach((author) => {
      if (selectedItems.length === 0) {
        count++;
        results += buildArticleCard(author);
      } else {
        for (let i = 0; i < selectedItems.length; i++) {
          if (author.restaurant === selectedItems[i]) {
            count++;
            results += buildArticleCard(author);
            break;
          }
        }
      }
    });
    return { count, results };
  }

  function prepareCuisineFilterData(data, selectedItems) {
    let results = '';
    let count = 0;
    data.forEach((author) => {
      if (selectedItems.length === 0) {
        count++;
        results += buildArticleCard(author);
      } else {
        for (let i = 0; i < selectedItems.length; i++) {
          if (author.restaurant_cuisine && author.restaurant_cuisine.indexOf(selectedItems[i]) !== -1) {
            count++;
            results += buildArticleCard(author);
            break;
          }
        }
      }
    });
    return { count, results };
  }

  function prepareSpaceFilterData(data, selectedItems) {
    let results = '';
    let count = 0;
    data.forEach((author) => {
      if (selectedItems.length === 0) {
        count++;
        results += buildArticleCard(author);
      } else {
        for (let i = 0; i < selectedItems.length; i++) {
          if (author.restaurant_space && author.restaurant_space.indexOf(selectedItems[i]) !== -1) {
            count++;
            results += buildArticleCard(author);
            break;
          }
        }
      }
    });
    return { count, results };
  }

  function getCheckedValues(name) {
    return Array.from(document.getElementsByName(name))
      .filter((el) => (/** @type {HTMLInputElement} */ (el)).type === 'checkbox' && (/** @type {HTMLInputElement} */ (el)).checked)
      .map((el) => (/** @type {HTMLInputElement} */ (el)).value);
  }

  function setSearchBoxError(selector, add) {
    const box = qs(selector);
    if (box) box.classList.toggle('error_active_block', !!add);
  }
  function setMsgPanel(selector, text) {
    const panel = qs(selector + ' .msg_panel');
    if (panel) panel.textContent = text;
  }

  function closeModal() {
    const popup = qs('.popup_filter_section');
    if (popup) popup.classList.remove('open');
    const wrapper = qs('.modal-wrapper-outer');
    if (wrapper) {
      wrapper.classList.add('hide');
      wrapper.setAttribute('aria-hidden', 'true');
    }
    qsAll('.top_bar_filter_section .filter_item[aria-controls]', document).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.remove('modal-active');
    document.body.classList.remove('modal-active');
  }

  function openModal() {
    const popup = qs('.popup_filter_section');
    if (popup) popup.classList.add('open');
    document.documentElement.classList.add('modal-active');
    document.body.classList.add('modal-active');
    const wrapper = qs('.modal-wrapper-outer');
    if (wrapper) {
      wrapper.classList.remove('hide');
      wrapper.setAttribute('aria-hidden', 'false');
    }
  }

  globalWindow.filter_restaurant = function () {
    const selectedItems = getCheckedValues('restaurant_check');
    const outer = qs('body .outer_search_box');
    if (outer) outer.classList.toggle('error_active_box', selectedItems.length === 0);
    if (selectedItems.length > 0) closeModal();
    const showresults = prepareRestaurantFilterData(datum, selectedItems);

    const url = new URL(window.location.href);
      url.searchParams.delete('restaurant');
      selectedItems.forEach(val => {
        url.searchParams.append('restaurant', val);
      });
      window.history.replaceState({}, '', url);   

    counterResults(showresults);
  };

  globalWindow.filter_restaurant_cuisine = function () {
    const selectedItems = getCheckedValues('restaurant_cuisine_check');
    const outer = qs('body .outer_search_box');
    if (outer) outer.classList.toggle('error_active_box', selectedItems.length === 0);
    if (selectedItems.length > 0) closeModal();
    const showresults = prepareCuisineFilterData(datum, selectedItems);

      const url = new URL(window.location.href);
      url.searchParams.delete('restaurant-cuisine');
      selectedItems.forEach(val => {
        url.searchParams.append('restaurant-cuisine', val);
      });
      window.history.replaceState({}, '', url);          

    counterResults(showresults);
  };

  globalWindow.filter_restaurant_space = function () {
    const selectedItems = getCheckedValues('restaurant_space_check');
    const outer = qs('body .outer_search_box');
    if (outer) outer.classList.toggle('error_active_box', selectedItems.length === 0);
    if (selectedItems.length > 0) closeModal();
    const showresults = prepareSpaceFilterData(datum, selectedItems);

      const url = new URL(window.location.href);
      url.searchParams.delete('type-of-offer');
      selectedItems.forEach(val => {
        url.searchParams.append('type-of-offer', val);
      });
      window.history.replaceState({}, '', url);   

    counterResults(showresults);
  };

  globalWindow.showResults = function () {
    const zipEl = /** @type {HTMLInputElement | null} */ (document.getElementById('zip_code_search-box'));
    const guestEl = /** @type {HTMLInputElement | null} */ (document.getElementById('guests_search-box'));
    const searchZip = (zipEl && zipEl.value) ? zipEl.value.toLowerCase() : '';
    const searchGuest = (guestEl && guestEl.value) || '';
    const filterType = document.body.getAttribute('data-filter-type-selected');

    if (filterType === 'location' && searchZip.length < 4) {
      setMsgPanel('.location_zip_search_box', 'Error: Enter the zip code');
      setSearchBoxError('.location_zip_search_box', true);
      return;
    }
    if (filterType === 'guests' && !searchGuest) {
      setMsgPanel('.guests_search_box', 'Error: Enter the number guests');
      setSearchBoxError('.guests_search_box', true);
      return;
    }

    setMsgPanel('.location_zip_search_box', '');
    setMsgPanel('.guests_search_box', '');
    setSearchBoxError('.location_zip_search_box', false);
    setSearchBoxError('.guests_search_box', false);

    if (filterType === 'location') {
      closeModal();
      const locLabel = qs('.top_bar_filter_selected_option_section .f_search_location');
      if (locLabel) locLabel.textContent = searchZip;
      const locFilter = qs('.top_bar_filter_selected_option_section [data-filter-type="location"]');
      if (locFilter) locFilter.classList.add('active');

      /*
      const url = new URL(window.location.href);
      url.searchParams.delete('location');
        url.searchParams.append('location', searchZip);      
      window.history.replaceState({}, '', url);   
      */

    }
    if (filterType === 'guests') {
      closeModal();
      const guestLabel = qs('.top_bar_filter_selected_option_section .f_search_guests');
      if (guestLabel) guestLabel.textContent = searchGuest;
      const guestFilter = qs('.top_bar_filter_selected_option_section [data-filter-type="guests"]');
      if (guestFilter) guestFilter.classList.add('active');

      /*
      const url = new URL(window.location.href);
      url.searchParams.delete('guest');
        url.searchParams.append('guest', searchGuest);      
      window.history.replaceState({}, '', url);   
      */
    }

    let results = '';
    let count = 0;
    datum.forEach((author) => {
      const textZip = (author.restaurant_location_zip_code || '').toLowerCase();
      const matchesZip = searchZip && textZip.indexOf(searchZip) !== -1;
      const matchesGuests = searchGuest && guestInRange(author.restaurant_guests || '', searchGuest);
      if (matchesZip || matchesGuests) {
        count++;
        results += buildArticleCard(author);
      }
    });

    const section = qs('.top_bar_filter_selected_option_section');
    if (section) section.classList.toggle('active_search_selected_opt', count > 0);
    counterResults({ count, results });
  };

  globalWindow.showResultsAll = function () {
    const results = datum.map(buildArticleCard).join('');
    const section = qs('.top_bar_filter_selected_option_section');
    if (section) {
      qsAll('.f_search_restaurant, .f_search_location, .f_search_cuisine, .f_search_space, .f_search_guests', section).forEach((el) => { el.textContent = ''; });
      section.classList.remove('active_search_selected_opt');
      qsAll('.filter_item', section).forEach((el) => el.classList.remove('active'));
    }
    counterResults({ count: datum.length, results });
  };

  // ----- Event handlers (delegated) -----
  function init() {
    const hasFilter = document.querySelector('.top_bar_filter_section');
    const hasData = document.getElementById('blog_article_data');

    if (hasFilter && hasData) {
      loadPosts();
    }

    // Ajaxinate (optional – global from theme/app script)
    const AjaxinateConstructor = typeof window !== 'undefined' && /** @type {any} */ (window).Ajaxinate;
    if (document.getElementById('AjaxinateLoop') && AjaxinateConstructor) {
      document.addEventListener('DOMContentLoaded', () => {
        new AjaxinateConstructor({
          container: '#AjaxinateLoop',
          pagination: '#AjaxinatePagination',
          method: 'click',
          offset: 1000,
          callback: () => {}
        });
      });
    }

    document.body.setAttribute('data-filter-type-selected', '');

    on(document.body, 'click', '.load-more', function (e) {
      e.preventDefault();
      const column = document.getElementById('AjaxinateLoop');
      if (!column) return;
      const hidden = qsAll('article.blog-article-card', column).filter((el) => el.style.display === 'none');
      hidden.slice(0, LOAD_MORE_COUNT).forEach((el) => { el.style.display = ''; });
      const visibleNow = qsAll('article.blog-article-card', column).filter((el) => el.style.display !== 'none').length;
      if (visibleNow > 0) {
        visibleCount = visibleNow;
      }
      if (hidden.length <= LOAD_MORE_COUNT) {
        const loadMoreBtn = getLoadMoreBtn();
        if (loadMoreBtn) loadMoreBtn.style.visibility = 'hidden';
      }
      saveState();
    });

    on(document.body, 'touchstart', '.load-more', function (e) {
      e.preventDefault();
      this.click();
    });

    // When user clicks through to an article, persist current visibleCount + scrollY
    on(
      document.body,
      'click',
      '.blog-article-card a.blog-article-card-image-link, .blog-article-card a.btn',
      function () {
        const column = document.getElementById('AjaxinateLoop');
        if (column) {
          const visibleNow = qsAll('article.blog-article-card', column).filter((el) => el.style.display !== 'none').length;
          if (visibleNow > 0) {
            visibleCount = visibleNow;
          }
        }
        saveState();
      }
    );

    on(document.body, 'click', '.top_bar_filter_inner_section .filter_item', function () {
      const filterType = this.getAttribute('data-filter-type');
      const filterPanel = this.getAttribute('data-filter-panel');
      if (filterType === 'all') {
        qsAll('.inner_check_box_section .outer_search_box .form-checkbox-group input').forEach((input) => { input.checked = false; });
        if (typeof globalWindow.showResultsAll === 'function') globalWindow.showResultsAll();
        const zip = /** @type {HTMLInputElement | null} */ (document.getElementById('zip_code_search-box'));
        const guests = /** @type {HTMLInputElement | null} */ (document.getElementById('guests_search-box'));
        if (zip) zip.value = '';
        if (guests) guests.value = '';
      } else if (filterType) {
        qsAll('.top_bar_filter_section .filter_item[aria-controls]', document).forEach(function (btn) {
          btn.setAttribute('aria-expanded', btn === this ? 'true' : 'false');
        }.bind(this));
        document.body.classList.toggle('filter_checkbox_popup_panel', filterPanel === 'checkbox');
        openModal();
        document.body.setAttribute('data-filter-type-selected', filterType);
      }
    });

    on(document.body, 'click', '.modal-wrapper-outer .modal-close-btn', function (e) {
      e.preventDefault();
      e.stopPropagation();
      closeModal();
    });

    // Close modal when clicking outside the modal content (e.g. on backdrop)
    on(document.body, 'click', '.modal-wrapper-outer', function (e) {
      const wrapper = e.target instanceof Element ? e.target.closest('.modal-wrapper-outer') : null;
      if (!wrapper || wrapper.classList.contains('hide')) return;
      const modalContent = wrapper.querySelector('.modal-content');
      if (modalContent && e.target instanceof Element && !modalContent.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
      }
    });

    on(document.body, 'click', '.top_bar_filter_selected_option_section .f_search_clear_all', function () {
      if (typeof globalWindow.showResultsAll === 'function') globalWindow.showResultsAll();
      const zip = /** @type {HTMLInputElement | null} */ (document.getElementById('zip_code_search-box'));
      const guests = /** @type {HTMLInputElement | null} */ (document.getElementById('guests_search-box'));
      if (zip) zip.value = '';
      if (guests) guests.value = '';
    });

    // Toggle .inner_search_box_section.is-active when user interacts with inner input (search or checkbox)
    function updateInnerSearchBoxActive(el) {
      const section = el && el.closest('.inner_search_box_section');
      if (!section) return;
      const isInput = el.matches('input[type="search"], input[type="text"], input[type="number"]');
      const isCheckbox = el.matches('input[type="checkbox"]');
      if (isInput) {
        section.classList.toggle('is-active', el.value.trim().length > 0 || document.activeElement === el);
      } else if (isCheckbox) {
        const anyChecked = section.querySelector('input[type="checkbox"]:checked');
        section.classList.toggle('is-active', !!anyChecked);
      }
    }
    document.body.addEventListener('focus', function (e) {
      const target = e.target instanceof Element ? e.target : null;
      const section = target && target.closest('.inner_search_box_section');
      if (section) section.classList.add('is-active');
    }, true);
    document.body.addEventListener('input', function (e) {
      const t = e.target instanceof Element ? e.target : null;
      if (t && t.closest('.inner_search_box_section')) updateInnerSearchBoxActive(t);
    }, true);
    document.body.addEventListener('change', function (e) {
      const t = e.target instanceof Element ? e.target : null;
      if (t && t.closest('.inner_search_box_section')) updateInnerSearchBoxActive(t);
    }, true);
    document.body.addEventListener('blur', function (e) {
      const t = e.target instanceof Element ? e.target : null;
      if (t && t.closest('.inner_search_box_section')) updateInnerSearchBoxActive(t);
    }, true);

    // Blur-up: when card image loads, add .is-loaded so full-res fades in over placeholder
    document.addEventListener('load', function (e) {
      const img = /** @type {Element} */ (e.target);
      if (img && img.classList && img.classList.contains('blog-card-img')) {
        const wrapper = img.closest('.blog-article-card-image-wrapper--blur-up');
        if (wrapper) wrapper.classList.add('is-loaded');
      }
    }, true);

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const params = new URLSearchParams(window.location.search);
    const get_url_restaurant_selected = params.getAll('restaurant');
    const get_url_restaurant_cuisine_selected = params.getAll('restaurant-cuisine');
    const get_url_restaurant_location_selected = params.getAll('location');
    const get_url_restaurant_guest_selected = params.getAll('guest');    
    const get_url_restaurant_offer_selected = params.getAll('type-of-offer');

    if (get_url_restaurant_selected && get_url_restaurant_selected.length > 0) {    
      document.querySelectorAll('input[name="restaurant_check"]').forEach(input => {
        if (get_url_restaurant_selected.includes(input.value)) {
          input.checked = true;
        }
      });
        setTimeout(() => {
          //alert("filter_restaurant");
          if (typeof globalWindow.filter_restaurant === "function") {
            globalWindow.filter_restaurant();
          } else {
            console.log("filter_restaurant not found");
          }
        }, 1000);       
    }

    if (get_url_restaurant_cuisine_selected && get_url_restaurant_cuisine_selected.length > 0) {    
      document.querySelectorAll('input[name="restaurant_cuisine_check"]').forEach(input => {
        if (get_url_restaurant_cuisine_selected.includes(input.value)) {
          input.checked = true;
        }
      });
        setTimeout(() => {
          //alert("filter_restaurant");
          if (typeof globalWindow.filter_restaurant === "function") {
            globalWindow.filter_restaurant_cuisine();
          } else {
            console.log("filter_restaurant_cuisine not found");
          }
        }, 1000);       
    }  
    
    if (get_url_restaurant_location_selected && get_url_restaurant_location_selected.length > 0) {    
      document.querySelector('#zip_code_search-box').value = get_url_restaurant_location_selected;

        setTimeout(() => {
          
          const locLabel = qs('.top_bar_filter_selected_option_section .f_search_location');
          if (locLabel) locLabel.textContent = get_url_restaurant_location_selected;
          const locFilter = qs('.top_bar_filter_selected_option_section [data-filter-type="location"]');
          if (locFilter) locFilter.classList.add('active');


        }, 1000);       
    }      


    if (get_url_restaurant_offer_selected && get_url_restaurant_offer_selected.length > 0) {    
      document.querySelectorAll('input[name="restaurant_space_check"]').forEach(input => {
        if (get_url_restaurant_offer_selected.includes(input.value)) {
          input.checked = true;
        }
      });
      

        setTimeout(() => {
          //alert("filter_restaurant_space");
          if (typeof globalWindow.filter_restaurant_space === "function") {
            globalWindow.filter_restaurant_space();
          } else {
            console.log("filter_restaurant not found");
          }
        }, 1000);  

       // setTimeout(() => {
          //if (typeof globalWindow.filter_restaurant === "function") {
       //     globalWindow.filter_restaurant_space();
         // } else {
         //   console.log("filter_restaurant not found");
         // }
       // }, 500);    
        
    }


  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
